import PeriodComparisonFilter, {
    PeriodSelection,
} from '@/components/PeriodComparisonFilter'
import { SCHEMES } from '@/components/EchartCustomized'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import ChartIcons from '@/icons/ChartIcons'
import GridIcons from '@/icons/GridIcons'
import dayjs from 'dayjs'
import { Sparkles } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, IconButton } from 'vnetwork-ui'
import { getProfitabilityRatios } from '../../services'
import {
    createDataHash,
    generateCacheKey,
    getCachedAnalysis,
} from '../../services/aiCache'
import {
    analyzeProfitability,
    GeminiReportResponse,
} from '../../services/gemini'
import { ProfitabilityRatiosParams } from '../../types'
import AIAnalysisReport from '../AIAnalysisReport'

interface ProfitabilityTabProps {
    filters?: { gte?: string; lte?: string }
}

type MarginKey =
    | 'gross_profit_margin'
    | 'operating_profit_margin'
    | 'other_income_margin'
    | 'net_profit_margin'

interface MarginConfig {
    key: MarginKey
    label: string
    color: string
}

const ProfitabilityTab: React.FC<ProfitabilityTabProps> = ({ filters }) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')
    const [comparisonViewMode, setComparisonViewMode] = useState<
        'chart' | 'table'
    >('chart')

    // AI Analysis states
    const [aiReport, setAiReport] = useState<GeminiReportResponse | null>(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState<string | null>(null)
    const [aiFromCache, setAiFromCache] = useState(false)
    const [showAiReport, setShowAiReport] = useState(false)

    // Margins configuration
    const margins: MarginConfig[] = [
        {
            key: 'gross_profit_margin',
            label: t('gross_profit_margin'),
            color: SCHEMES.brand[4], // Info Blue
        },
        {
            key: 'operating_profit_margin',
            label: t('operating_profit_margin'),
            color: SCHEMES.warning[0], // Warning Orange
        },
        {
            key: 'other_income_margin',
            label: t('other_income_margin'),
            color: SCHEMES.success[0], // Success Green
        },
        {
            key: 'net_profit_margin',
            label: t('net_profit_margin'),
            color: SCHEMES.error[0], // Error Red
        },
    ]

    // Period comparison filter state
    const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({
        startDate: filters?.gte || dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: filters?.lte || dayjs().endOf('year').format('YYYY-MM-DD'),
        viewMode: 'quarter',
        selectedPeriods: new Set([
            'quarter_1',
            'quarter_2',
            'quarter_3',
            'quarter_4',
        ]),
        selectedMetrics: new Set(margins.map((m) => m.key)),
    })

    const params: ProfitabilityRatiosParams = useMemo(
        () => ({
            from_month: filters?.gte || '',
            to_month: filters?.lte || '',
            tax_rate: 0.5,
        }),
        [filters]
    )

    // Fetch data
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_PROFITABILITY_RATIOS, params],
        queryFn: () => getProfitabilityRatios(params),
        staleTime: 5 * 60 * 1000,
        placeholderData: true,
    })

    // Visible margins state
    const [visibleMargins, setVisibleMargins] = useState<Set<MarginKey>>(
        new Set(margins.map((m) => m.key))
    )

    // Auto-load cached AI report when data changes
    useEffect(() => {
        if (!data || data.length === 0) return

        // Check if there's a cached report for this data
        const dataHash = createDataHash(data)
        const cacheKey = generateCacheKey({
            type: 'profitability',
            period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
            selected_metrics: Array.from(visibleMargins),
            data_hash: dataHash,
        })

        const cachedReport = getCachedAnalysis(cacheKey)
        if (cachedReport) {
            setAiReport(cachedReport)
            setAiFromCache(true)
            setShowAiReport(true)
            setAiError(null)
        }
    }, [data, filters?.gte, filters?.lte, visibleMargins])

    // Toggle margin visibility
    const toggleMargin = (key: MarginKey) => {
        setVisibleMargins((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle all margins
    const toggleAll = () => {
        if (visibleMargins.size === margins.length) {
            setVisibleMargins(new Set())
        } else {
            setVisibleMargins(new Set(margins.map((m) => m.key)))
        }
    }

    // Prepare chart data with filled missing months
    const chartData = useMemo(() => {
        if (!data || !filters?.gte || !filters?.lte) return []

        // Generate all months in the range
        const allMonths: string[] = []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        let current = start

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            allMonths.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }

        // Create a map of existing data
        const dataMap = new Map(data.map((item) => [item.report_month, item]))

        // Fill all months with data or zeros
        return allMonths.map((month) => {
            const existingData = dataMap.get(month)
            return {
                month,
                gross_profit_margin: existingData?.gross_profit_margin || 0,
                operating_profit_margin:
                    existingData?.operating_profit_margin || 0,
                other_income_margin: existingData?.other_income_margin || 0,
                net_profit_margin: existingData?.net_profit_margin || 0,
            }
        })
    }, [data, filters?.gte, filters?.lte])

    // Prepare comparison data
    const comparisonData = useMemo(() => {
        if (!chartData.length) return { years: [], quarters: [], months: [] }

        // Group data by year, quarter, and month
        const yearlyData: Record<string, { [key in MarginKey]: number[] }> = {}
        const quarterlyData: Record<string, { [key in MarginKey]: number[] }> =
            {}
        const monthlyData: Record<string, { [key in MarginKey]: number }> = {}

        chartData.forEach((item) => {
            const [year, month] = item.month.split('-')
            const monthNum = parseInt(month)
            const quarter = Math.ceil(monthNum / 3)
            const quarterKey = `Q${quarter} ${year}`
            const monthKey = `T${monthNum} ${year}`

            // Yearly aggregation (average)
            if (!yearlyData[year]) {
                yearlyData[year] = {
                    gross_profit_margin: [],
                    operating_profit_margin: [],
                    other_income_margin: [],
                    net_profit_margin: [],
                }
            }
            margins.forEach((margin) => {
                yearlyData[year][margin.key].push(item[margin.key])
            })

            // Quarterly aggregation (average)
            if (!quarterlyData[quarterKey]) {
                quarterlyData[quarterKey] = {
                    gross_profit_margin: [],
                    operating_profit_margin: [],
                    other_income_margin: [],
                    net_profit_margin: [],
                }
            }
            margins.forEach((margin) => {
                quarterlyData[quarterKey][margin.key].push(item[margin.key])
            })

            // Monthly data
            monthlyData[monthKey] = {
                gross_profit_margin: item.gross_profit_margin,
                operating_profit_margin: item.operating_profit_margin,
                other_income_margin: item.other_income_margin,
                net_profit_margin: item.net_profit_margin,
            }
        })

        // Calculate averages for years and quarters
        const calculateAverage = (arr: number[]) =>
            arr.reduce((sum, val) => sum + val, 0) / arr.length

        return {
            years: Object.keys(yearlyData)
                .sort()
                .map((year) => ({
                    period: year,
                    data: {
                        gross_profit_margin: calculateAverage(
                            yearlyData[year].gross_profit_margin
                        ),
                        operating_profit_margin: calculateAverage(
                            yearlyData[year].operating_profit_margin
                        ),
                        other_income_margin: calculateAverage(
                            yearlyData[year].other_income_margin
                        ),
                        net_profit_margin: calculateAverage(
                            yearlyData[year].net_profit_margin
                        ),
                    },
                })),
            quarters: Object.keys(quarterlyData)
                .sort((a, b) => {
                    const [qA, yearA] = a.split(' ')
                    const [qB, yearB] = b.split(' ')
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return qA.localeCompare(qB)
                })
                .map((quarter) => ({
                    period: quarter,
                    data: {
                        gross_profit_margin: calculateAverage(
                            quarterlyData[quarter].gross_profit_margin
                        ),
                        operating_profit_margin: calculateAverage(
                            quarterlyData[quarter].operating_profit_margin
                        ),
                        other_income_margin: calculateAverage(
                            quarterlyData[quarter].other_income_margin
                        ),
                        net_profit_margin: calculateAverage(
                            quarterlyData[quarter].net_profit_margin
                        ),
                    },
                })),
            months: Object.keys(monthlyData)
                .sort((a, b) => {
                    const [tA, yearA] = a.split(' ')
                    const [tB, yearB] = b.split(' ')
                    const monthA = parseInt(tA.replace('T', ''))
                    const monthB = parseInt(tB.replace('T', ''))
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return monthA - monthB
                })
                .map((month) => ({
                    period: month,
                    data: monthlyData[month],
                })),
        }
    }, [chartData, margins])

    // Filter comparison data based on periodSelection
    const filteredComparisonData = useMemo(() => {
        if (periodSelection.viewMode === 'year') {
            return {
                years: comparisonData.years.filter((y) =>
                    periodSelection.selectedPeriods.has(y.period)
                ),
                quarters: [],
                months: [],
            }
        } else if (periodSelection.viewMode === 'quarter') {
            return {
                years: [],
                quarters: comparisonData.quarters.filter((q) => {
                    const quarter = q.period.split(' ')[0] // Get 'Q1', 'Q2', etc
                    return periodSelection.selectedPeriods.has(quarter)
                }),
                months: [],
            }
        } else {
            // month mode
            return {
                years: [],
                quarters: [],
                months: comparisonData.months.filter((m) => {
                    const monthNum = m.period.split(' ')[0].replace('T', '')
                    return periodSelection.selectedPeriods.has(monthNum)
                }),
            }
        }
    }, [comparisonData, periodSelection])

    // Prepare chart data for comparison
    const comparisonChartData = useMemo(() => {
        let periods: {
            period: string
            data: { [key in MarginKey]: number }
        }[] = []

        if (periodSelection.viewMode === 'year') {
            periods = filteredComparisonData.years
        } else if (periodSelection.viewMode === 'quarter') {
            periods = filteredComparisonData.quarters.slice(-8)
        } else {
            periods = filteredComparisonData.months.slice(-12)
        }

        return periods.map((p) => {
            const item: any = { period: p.period }
            margins.forEach((margin) => {
                if (periodSelection.selectedMetrics.has(margin.key)) {
                    item[margin.key] = p.data[margin.key]
                }
            })
            return item
        })
    }, [filteredComparisonData, periodSelection, margins])

    // Calculate growth percentage
    const calculateGrowth = (current: number, previous: number) => {
        if (previous === 0) return 0
        return ((current - previous) / Math.abs(previous)) * 100
    }

    // Format percentage
    const formatPercentage = (value: number) => {
        const formatted = value.toFixed(1)
        return value > 0 ? `+${formatted}%` : `${formatted}%`
    }

    // Format number for display
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('vi-VN', {
            maximumFractionDigits: 2,
        }).format(num)
    }

    // Format month for display
    const formatMonth = (monthStr: string | number | undefined) => {
        if (!monthStr) return ''
        const str = String(monthStr)
        const [year, month] = str.split('-')
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${monthNames[parseInt(month) - 1]} ${year}`
    }

    // Handle AI Analysis
    const handleAIAnalysis = async () => {
        if (!data || data.length === 0) {
            setAiError('Không có dữ liệu để phân tích')
            setShowAiReport(true)
            return
        }

        setShowAiReport(true)
        setAiLoading(true)
        setAiError(null)
        setAiReport(null)
        setAiFromCache(false)

        try {
            // Check cache first for instant result
            const dataHash = createDataHash(data)
            const cacheKey = generateCacheKey({
                type: 'profitability',
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMargins),
                data_hash: dataHash,
            })

            const cachedReport = getCachedAnalysis(cacheKey)
            if (cachedReport) {
                setAiReport(cachedReport)
                setAiFromCache(true)
                setAiLoading(false)
                return
            }

            // If no cache, fetch from API
            const report = await analyzeProfitability({
                data,
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleMargins),
            })
            setAiReport(report)
            setAiFromCache(false)
        } catch (error: any) {
            console.error('AI Analysis Error:', error)
            setAiError(
                error?.message ||
                    'Không thể kết nối với dịch vụ AI. Vui lòng thử lại sau.'
            )
        } finally {
            setAiLoading(false)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header with filters and view toggle */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-end mb-2">
                    <div className="flex gap-2">
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleAIAnalysis}
                            className="flex items-center gap-2"
                            disabled={!data || data.length === 0}
                        >
                            <Sparkles className="w-4 h-4" />
                            {t('analyze_with_ai')}
                        </Button>
                        <IconButton
                            size="small"
                            color={viewMode === 'chart' ? 'primary' : 'default'}
                            onClick={() => setViewMode('chart')}
                            className="border"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={viewMode === 'table' ? 'primary' : 'default'}
                            onClick={() => setViewMode('table')}
                            className="border"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Margin Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                    {/* Select All */}
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="select-all"
                            checked={visibleMargins.size === margins.length}
                            onCheckedChange={toggleAll}
                        />
                        <Label
                            htmlFor="select-all"
                            className="text-sm cursor-pointer"
                        >
                            {t('select_all')}
                        </Label>
                    </div>

                    {/* Individual Margins */}
                    {margins.map((margin) => (
                        <div
                            key={margin.key}
                            className="flex items-center gap-2"
                        >
                            <Checkbox
                                id={margin.key}
                                checked={visibleMargins.has(margin.key)}
                                onCheckedChange={() => toggleMargin(margin.key)}
                                style={
                                    visibleMargins.has(margin.key)
                                        ? {
                                              backgroundColor: margin.color,
                                              borderColor: margin.color,
                                          }
                                        : {
                                              borderColor: margin.color,
                                          }
                                }
                            />
                            <Label
                                htmlFor={margin.key}
                                className="text-sm cursor-pointer"
                                style={{
                                    color: visibleMargins.has(margin.key)
                                        ? margin.color
                                        : undefined,
                                }}
                            >
                                {margin.label}
                            </Label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart View */}
            {viewMode === 'chart' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <ResponsiveContainer width="100%" height={500}>
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis
                                tickFormatter={(value) =>
                                    `${value.toFixed(1)}%`
                                }
                                tick={{ fontSize: 12 }}
                                domain={[0, 100]}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                                                <p className="font-bold mb-2">
                                                    {formatMonth(label)}
                                                </p>
                                                {payload.map((entry: any) => (
                                                    <div
                                                        key={entry.dataKey}
                                                        className="flex justify-between items-center gap-8 py-1"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="w-3 h-3 rounded-full"
                                                                style={{
                                                                    backgroundColor:
                                                                        entry.color,
                                                                }}
                                                            />
                                                            <span className="text-sm">
                                                                {entry.name}
                                                            </span>
                                                        </div>
                                                        <span className="font-bold text-sm">
                                                            {formatNumber(
                                                                entry.value
                                                            )}
                                                            %
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    }
                                    return null
                                }}
                            />
                            <Legend
                                wrapperStyle={{ fontSize: '14px' }}
                                iconType="line"
                            />

                            {margins.map((margin) =>
                                visibleMargins.has(margin.key) ? (
                                    <Line
                                        key={margin.key}
                                        type="monotone"
                                        dataKey={margin.key}
                                        name={margin.label}
                                        stroke={margin.color}
                                        strokeWidth={1}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                    />
                                ) : null
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                                    {t('month')}
                                </th>
                                {margins
                                    .filter((m) => visibleMargins.has(m.key))
                                    .map((margin) => (
                                        <th
                                            key={margin.key}
                                            className="text-right py-3 px-4 font-semibold text-gray-700"
                                        >
                                            {margin.label}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {chartData.map((row, idx) => (
                                <tr
                                    key={idx}
                                    className="border-b border-gray-100 hover:bg-gray-50"
                                >
                                    <td className="py-3 px-4">
                                        {formatMonth(row.month)}
                                    </td>
                                    {margins
                                        .filter((m) =>
                                            visibleMargins.has(m.key)
                                        )
                                        .map((margin) => (
                                            <td
                                                key={margin.key}
                                                className="text-right py-3 px-4"
                                            >
                                                {formatNumber(row[margin.key])}%
                                            </td>
                                        ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* AI Analysis Report - Inline */}
            {showAiReport && (
                <AIAnalysisReport
                    report={aiReport}
                    loading={aiLoading}
                    error={aiError}
                    fromCache={aiFromCache}
                />
            )}

            {/* Period Comparison Filter */}
            <PeriodComparisonFilter
                metrics={margins}
                onChange={setPeriodSelection}
                defaultStartDate={
                    filters?.gte || dayjs().startOf('year').format('YYYY-MM-DD')
                }
                defaultEndDate={
                    filters?.lte || dayjs().endOf('year').format('YYYY-MM-DD')
                }
            />

            <div className="bg-white border border-gray-200 rounded-lg p-6">
                {/* Header with view toggle */}
                <div className="flex items-center justify-end mb-2">
                    <div className="flex gap-2">
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'chart'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('chart')}
                            className="border"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'table'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('table')}
                            className="border"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Chart View */}
                {comparisonViewMode === 'chart' &&
                    comparisonChartData.length > 0 && (
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                data={comparisonChartData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="period"
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis
                                    tickFormatter={(value) =>
                                        `${value.toFixed(1)}%`
                                    }
                                    tick={{ fontSize: 12 }}
                                    domain={[0, 100]}
                                />
                                <Tooltip
                                    content={({ active, payload, label }) => {
                                        if (
                                            active &&
                                            payload &&
                                            payload.length
                                        ) {
                                            return (
                                                <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                                                    <p className="font-bold mb-2">
                                                        {label}
                                                    </p>
                                                    {payload.map(
                                                        (entry: any) => (
                                                            <div
                                                                key={
                                                                    entry.dataKey
                                                                }
                                                                className="flex justify-between items-center gap-8 py-1"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <div
                                                                        className="w-3 h-3 rounded-full"
                                                                        style={{
                                                                            backgroundColor:
                                                                                entry.color,
                                                                        }}
                                                                    />
                                                                    <span className="text-sm">
                                                                        {
                                                                            entry.name
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <span className="font-bold text-sm">
                                                                    {formatNumber(
                                                                        entry.value
                                                                    )}
                                                                    %
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )
                                        }
                                        return null
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: '14px' }} />

                                {margins
                                    .filter((m) =>
                                        periodSelection.selectedMetrics.has(
                                            m.key
                                        )
                                    )
                                    .map((margin) => (
                                        <Bar
                                            key={margin.key}
                                            dataKey={margin.key}
                                            name={margin.label}
                                            fill={margin.color}
                                        />
                                    ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                {/* Table View */}
                {comparisonViewMode === 'table' && (
                    <>
                        {/* Year Comparison */}
                        {filteredComparisonData.years.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('year_over_year')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.years.map(
                                                    (year, idx) => (
                                                        <th
                                                            key={year.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600"
                                                        >
                                                            {year.period}
                                                            {idx > 0 && (
                                                                <span className="ml-2 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        filteredComparisonData
                                                                            .years[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {margins
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((margin) => (
                                                    <tr
                                                        key={margin.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {margin.label}
                                                        </td>
                                                        {filteredComparisonData.years.map(
                                                            (year, idx) => {
                                                                const value =
                                                                    year.data[
                                                                        margin
                                                                            .key
                                                                    ]
                                                                const prevValue =
                                                                    idx > 0
                                                                        ? filteredComparisonData
                                                                              .years[
                                                                              idx -
                                                                                  1
                                                                          ]
                                                                              .data[
                                                                              margin
                                                                                  .key
                                                                          ]
                                                                        : null
                                                                const growth =
                                                                    prevValue !==
                                                                    null
                                                                        ? calculateGrowth(
                                                                              value,
                                                                              prevValue
                                                                          )
                                                                        : null

                                                                return (
                                                                    <td
                                                                        key={
                                                                            year.period
                                                                        }
                                                                        className="text-right py-2 px-3"
                                                                    >
                                                                        <div>
                                                                            {formatNumber(
                                                                                value
                                                                            )}
                                                                            %
                                                                        </div>
                                                                        {growth !==
                                                                            null && (
                                                                            <div
                                                                                className={`text-xs ${
                                                                                    growth >
                                                                                    0
                                                                                        ? 'text-green-600'
                                                                                        : growth <
                                                                                            0
                                                                                          ? 'text-red-600'
                                                                                          : 'text-gray-500'
                                                                                }`}
                                                                            >
                                                                                {formatPercentage(
                                                                                    growth
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                )
                                                            }
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Quarter Comparison */}
                        {filteredComparisonData.quarters.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('quarter_over_quarter')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.quarters
                                                    .slice(-8)
                                                    .map(
                                                        (quarter, idx, arr) => (
                                                            <th
                                                                key={
                                                                    quarter.period
                                                                }
                                                                className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                            >
                                                                {quarter.period}
                                                                {idx > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">
                                                                        (vs{' '}
                                                                        {
                                                                            arr[
                                                                                idx -
                                                                                    1
                                                                            ]
                                                                                .period
                                                                        }
                                                                        )
                                                                    </span>
                                                                )}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {margins
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((margin) => (
                                                    <tr
                                                        key={margin.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {margin.label}
                                                        </td>
                                                        {filteredComparisonData.quarters
                                                            .slice(-8)
                                                            .map(
                                                                (
                                                                    quarter,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        quarter
                                                                            .data[
                                                                            margin
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  margin
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                quarter.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}

                                                                                %
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Month Comparison */}
                        {filteredComparisonData.months.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('month_over_month')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.months
                                                    .slice(-12)
                                                    .map((month, idx, arr) => (
                                                        <th
                                                            key={month.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                        >
                                                            {month.period}
                                                            {idx > 0 && (
                                                                <span className="ml-1 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        arr[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {margins
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((margin) => (
                                                    <tr
                                                        key={margin.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {margin.label}
                                                        </td>
                                                        {filteredComparisonData.months
                                                            .slice(-12)
                                                            .map(
                                                                (
                                                                    month,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        month
                                                                            .data[
                                                                            margin
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  margin
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                month.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}

                                                                                %
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default ProfitabilityTab
